import React from 'react';
import { Hourglass, Wrench } from 'lucide-react';
import './FuturaRetornoBoardBellFilterButton.css';
import '../toolbar/BoardClickUpFilterChip.css';

function formatTechnicianTitleCase(name) {
  if (!name || typeof name !== 'string') return '';
  return name
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

/**
 * Botão estilo refresh-btn na barra de filtros: sino (Bootstrap Icons).
 * Pendente: bolinha; Liberado: tom suave + balanço (prioridade). Clique abre resumo compacto.
 */
export function FuturaRetornoBoardBellFilterButton({
  tabsEnabled,
  mode,
  disabled = false,
  techniciansSummary = [],
  onTechnicianColumnActivate,
  /** Controlado (opcional): estado aberto do menu de notificações */
  open: openProp,
  onOpenChange,
}) {
  const isLiberado = mode === 'liberado';
  const isPendente = mode === 'pendente';
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = openProp !== undefined;
  const isOpen = isControlled ? !!openProp : internalOpen;
  const setIsOpen = React.useCallback(
    (nextOrFn) => {
      const next =
        typeof nextOrFn === 'function' ? nextOrFn(isOpen) : nextOrFn;
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, isOpen, onOpenChange]
  );
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    if (!isLiberado) {
      setIsAnimating(false);
      return undefined;
    }

    // Disparo imediato + repetição a cada 4s.
    setIsAnimating(true);
    let stopTimeout = window.setTimeout(() => setIsAnimating(false), 2000);
    const intervalId = window.setInterval(() => {
      setIsAnimating(true);
      window.clearTimeout(stopTimeout);
      stopTimeout = window.setTimeout(() => setIsAnimating(false), 2000);
    }, 4000);

    return () => {
      window.clearTimeout(stopTimeout);
      window.clearInterval(intervalId);
    };
  }, [isLiberado]);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const onDocumentEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocumentPointerDown);
    document.addEventListener('keydown', onDocumentEscape);

    return () => {
      document.removeEventListener('mousedown', onDocumentPointerDown);
      document.removeEventListener('keydown', onDocumentEscape);
    };
  }, [isOpen]);
  const title = isLiberado
    ? 'Existem retornos liberados em alguma coluna de técnico'
    : isPendente
      ? 'Existem retornos pendentes em alguma coluna de técnico'
      : 'Alertas de retorno na visão FUTURA (Pendente / Liberado)';
  const hasRows = techniciansSummary.length > 0;

  if (!tabsEnabled) return null;

  return (
    <div ref={rootRef} className="futura-retorno-board-bell-filter-btn__container board-clickup-icon-btn-anchor">
      <button
        type="button"
        className={[
          'board-clickup-filter-chip',
          'board-clickup-filter-chip--icon-only',
          'board-clickup-filter-chip--bell',
          'futura-retorno-board-bell-filter-btn',
          isLiberado ? 'futura-retorno-board-bell-filter-btn--liberado' : '',
          isPendente ? 'futura-retorno-board-bell-filter-btn--pendente' : '',
          isAnimating ? 'futura-retorno-board-bell-filter-btn--animating' : '',
          isOpen ? 'futura-retorno-board-bell-filter-btn--open is-open' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={title}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setIsOpen((prev) => !prev);
        }}
      >
        <span className="board-clickup-filter-chip-icon futura-retorno-board-bell-filter-btn__icon-wrap">
          <i className="bi bi-bell futura-retorno-board-bell-filter-btn__icon" aria-hidden />
          {isPendente ? <span className="futura-retorno-board-bell-filter-btn__dot" aria-hidden /> : null}
        </span>
      </button>

      {!isOpen ? (
        <span className="board-clickup-tooltip board-clickup-tooltip--bottom" role="tooltip">
          Notificações de pendência técnica
        </span>
      ) : null}

      {isOpen ? (
        <div
          className="futura-retorno-board-bell-filter-btn__menu"
          role="dialog"
          aria-label="Pendências e peças liberadas"
        >
          <header className="futura-retorno-board-bell-filter-btn__menu-head">
            <div className="futura-retorno-board-bell-filter-btn__menu-title">
              Pendências e peças liberadas
            </div>
          </header>
          {hasRows ? (
            <div className="futura-retorno-board-bell-filter-btn__menu-body">
              {techniciansSummary.map((row) => (
                               <section key={row.technician} className="futura-retorno-board-bell-filter-btn__tech-block">
                  <div className="futura-retorno-board-bell-filter-btn__tech-subtitle">
                    {typeof onTechnicianColumnActivate === 'function' ? (
                      <button
                        type="button"
                        className="futura-retorno-board-bell-filter-btn__tech-name futura-retorno-board-bell-filter-btn__tech-name--action"
                        title="Mostrar apenas esta coluna no quadro"
                        onClick={() => {
                          onTechnicianColumnActivate(row.technician);
                          setIsOpen(false);
                        }}
                      >
                        {formatTechnicianTitleCase(row.technician)}
                      </button>
                    ) : (
                      <div className="futura-retorno-board-bell-filter-btn__tech-name">
                        {formatTechnicianTitleCase(row.technician)}
                      </div>
                    )}
                    <ul className="futura-retorno-board-bell-filter-btn__metric-list">
                      <li className="futura-retorno-board-bell-filter-btn__metric-list-item">
                        <Hourglass
                          className="futura-retorno-board-bell-filter-btn__metric-icon"
                          size={8}
                          strokeWidth={1.5}
                          aria-hidden
                        />
                        <span className="futura-retorno-board-bell-filter-btn__metric-label">Pendências</span>
                        <span className="futura-retorno-board-bell-filter-btn__metric-value">{row.pendente}</span>
                      </li>
                      <li
                        className={`futura-retorno-board-bell-filter-btn__metric-list-item${
                          row.liberado > 0
                            ? ' futura-retorno-board-bell-filter-btn__metric-list-item--liberado-pulse'
                            : ''
                        }`}
                      >
                        <Wrench
                          className="futura-retorno-board-bell-filter-btn__metric-icon futura-retorno-board-bell-filter-btn__metric-icon--liberado"
                          size={8}
                          strokeWidth={1.5}
                          aria-hidden
                        />
                        <span className="futura-retorno-board-bell-filter-btn__metric-label">
                          Pendências liberadas
                        </span>
                        <span className="futura-retorno-board-bell-filter-btn__metric-value">{row.liberado}</span>
                      </li>
                    </ul>
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="futura-retorno-board-bell-filter-btn__menu-empty">
              Nenhuma pendência no momento
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
